import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { 
  Phone, Mail, Calendar, Building2, ArrowRight, MessageSquare, 
  Trophy, X, BadgeCheck, User, MessageCircle, ShieldAlert, Clock, MapPin, RefreshCw
} from "lucide-react";
import { api, asItems, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { cleanNoteText } from "@/lib/noteText";

const STATES = [
  { key: "new", label: "New", color: "bg-blue-950/40 text-blue-200 border-blue-800", dot: "bg-sky-500", accent: "from-sky-500/60" },
  { key: "contacted", label: "Contacted", color: "bg-yellow-950/40 text-yellow-200 border-yellow-800", dot: "bg-amber-500", accent: "from-amber-500/60" },
  { key: "interested", label: "Interested", color: "bg-purple-950/40 text-purple-200 border-purple-800", dot: "bg-purple-500", accent: "from-purple-500/60" },
  { key: "site_visit_scheduled", label: "Site Visit Scheduled", color: "bg-indigo-950/40 text-indigo-200 border-indigo-800", dot: "bg-indigo-500", accent: "from-indigo-500/60" },
  { key: "negotiation", label: "Negotiation", color: "bg-orange-950/40 text-orange-200 border-orange-800", dot: "bg-orange-500", accent: "from-orange-500/60" },
  { key: "booked", label: "Booked", color: "bg-cyan-950/40 text-cyan-200 border-cyan-800", dot: "bg-cyan-500", accent: "from-cyan-500/60" },
  { key: "sold", label: "Sold", color: "bg-green-950/40 text-green-200 border-green-800", dot: "bg-emerald-500", accent: "from-emerald-500/60" },
  { key: "lost", label: "Lost", color: "bg-red-950/40 text-red-200 border-red-800", dot: "bg-rose-500", accent: "from-rose-500/60" },
];
const STATE_LOOKUP = Object.fromEntries(STATES.map(s => [s.key, s]));
const isMobileViewport = () =>
  typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;

const NEXT = {
  new: ["contacted", "lost"],
  contacted: ["interested", "lost"],
  interested: ["site_visit_scheduled", "negotiation", "lost"],
  site_visit_scheduled: ["negotiation", "lost"],
  negotiation: ["booked", "lost"],
  booked: ["sold", "lost"],
  sold: [],
  lost: ["contacted"],
};

// Plot-demand lifecycle and compatibility statuses do not all share the CRM
// Kanban vocabulary, so map them onto visible columns. The approved/rejected
// aliases keep older linked leads readable while V18 normalizes demand records.
const PLOT_STATUS_TO_COLUMN = {
  new: "new",
  matching: "interested",
  assigned: "contacted",
  contacted: "contacted",
  approved: "booked",
  completed: "sold",
  cancelled: "lost",
  rejected: "lost",
};
const columnKey = (lead) => {
  const raw = String(lead?.status || "new").toLowerCase();
  if (STATE_LOOKUP[raw]) return raw;
  return PLOT_STATUS_TO_COLUMN[raw] || "new";
};

export default function Leads() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedLead, setSelectedLead] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [mobileFilter, setMobileFilter] = useState("all");
  const [mobileDialogOpen, setMobileDialogOpen] = useState(false);
  const [selectedTargetState, setSelectedTargetState] = useState("");
  const [events, setEvents] = useState([]);
  const [eventsError, setEventsError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [proposalRejectReason, setProposalRejectReason] = useState("");
  const [offices, setOffices] = useState([]);
  const [agents, setAgents] = useState([]);
  const [directoryError, setDirectoryError] = useState("");
  const [searchParams] = useSearchParams();
  const [autoSelectDone, setAutoSelectDone] = useState(false);
  const requestedLeadId = searchParams.get("select") || "";
  const selectedLeadId = selectedLead?.id || selectedLead?.plot_request_id || selectedLead?.lead_id || selectedLead?._id || "";

  useEffect(() => { setProposalRejectReason(""); }, [selectedLeadId]);

  const userRole = String(user?.role || "").toLowerCase().replace("office_user", "office");
  const isAgent = userRole === "agent";
  const isAdmin = userRole === "admin" || userRole === "super_admin";
  const isOffice = userRole === "office";
  const currentUserId = user?.user_id || user?.id;
  const currentUserOfficeId = user?.office_id || user?.office?.id || user?.raw?.office_id || "";
  const currentUserOfficeName = user?.office_name || user?.office?.name || user?.raw?.office_name || "Current office";

  const isLeadAssignedToMe = (lead) => {
    if (!lead) return false;
    return String(lead.assigned_to_user_id || lead.assigned_agent_id) === String(currentUserId);
  };

  const canInteractWithLead = (lead) => {
    if (!lead) return false;
    return isAdmin || isOffice || isLeadAssignedToMe(lead);
  };

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    api.get("/leads")
      .then((res) => {
        const list = asItems(res.data);
        setLeads(list);
        // Refresh selected lead if open
        setSelectedLead((current) => {
          if (!current) return current;
          const fresh = list.find(l => (l.id || l.plot_request_id || l.lead_id || l._id) === (current.id || current.plot_request_id || current.lead_id || current._id));
          return fresh || current;
        });
      })
      .catch((requestError) => {
        setLeads([]);
        setError(formatApiError(requestError));
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadEvents = useCallback((id) => {
    if (!id) return Promise.resolve();
    setEventsError("");
    return api.get(`/leads/${encodeURIComponent(id)}/events`)
      .then((res) => setEvents(asItems(res.data)))
      .catch((requestError) => {
        setEvents([]);
        setEventsError(formatApiError(requestError));
      });
  }, []);

  // React Router can update only the query string without remounting this page.
  // Reset resolution for that case so every new ?select=<lead> link is honoured.
  useEffect(() => {
    setAutoSelectDone(false);
  }, [requestedLeadId]);

  // Deep-link support: when arriving with ?select=<id> (e.g. from the Admin
  // "View CRM Lead" link), auto-open that lead once the list has loaded.
  useEffect(() => {
    if (autoSelectDone) return;
    const selectId = requestedLeadId;
    if (!selectId || loading) return;
    const match = leads.find(
      (l) => String(l.id || l.plot_request_id || l.lead_id || l._id) === String(selectId)
    );
    if (match) {
      setSelectedLead(match);
      setSelectedTargetState("");
      setNote("");
      setEvents([]);
      setMobileDialogOpen(isMobileViewport());
      loadEvents(match.id || match.plot_request_id || match.lead_id || match._id);
      setAutoSelectDone(true);
      return;
    }
    // Admin links may point to a valid lead outside this capped Kanban feed.
    // Resolve that record exactly so every relationship link remains usable.
    if (!isAdmin) {
      setAutoSelectDone(true);
      return;
    }
    let current = true;
    api.get(`/v1/admin/lead-explorer/leads/${encodeURIComponent(selectId)}`)
      .then((res) => {
        if (!current) return;
        const exact = res.data?.data || res.data;
        setSelectedLead(exact);
        setSelectedTargetState("");
        setNote("");
        setEvents([]);
        setMobileDialogOpen(isMobileViewport());
        return loadEvents(selectId);
      })
      .catch((requestError) => { if (current) toast.error(formatApiError(requestError)); })
      .finally(() => { if (current) setAutoSelectDone(true); });
    return () => { current = false; };
  }, [leads, requestedLeadId, autoSelectDone, isAdmin, loading, loadEvents]);

  useEffect(() => {
    if (!isAdmin && !isOffice) return;
    let active = true;
    setDirectoryError("");
    const requests = isAdmin
      ? Promise.all([
          api.get("/v1/admin/directory/offices", { params: { limit: 200 } }),
          api.get("/v1/admin/directory/agents", { params: { limit: 200 } }),
        ]).then(([officeResponse, agentResponse]) => {
          if (active) {
            setOffices(asItems(officeResponse.data));
            setAgents(asItems(agentResponse.data));
          }
        })
      : api.get("/office/agents").then((agentResponse) => {
          if (active) {
            setOffices(currentUserOfficeId ? [{ id: currentUserOfficeId, office_id: currentUserOfficeId, name: currentUserOfficeName }] : []);
            setAgents(asItems(agentResponse.data));
          }
        });
    requests.catch((requestError) => {
      if (active) {
        setOffices([]);
        setAgents([]);
        setDirectoryError(formatApiError(requestError));
      }
    });
    return () => { active = false; };
  }, [isAdmin, isOffice, currentUserOfficeId, currentUserOfficeName]);

  const handleTransition = async (toState) => {
    if (!selectedLead) return;
    const id = selectedLead.id || selectedLead.plot_request_id || selectedLead.lead_id || selectedLead._id;
    setBusy(true);
    try {
      await api.post(`/leads/${id}/transition`, { status: toState, note: note.trim() });
      toast.success(`Status updated to ${STATE_LOOKUP[toState.toLowerCase()]?.label || toState}`);
      setNote("");
      setSelectedTargetState("");
      loadEvents(id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteLead = async (lead) => {
    if (!lead) return;
    const id = lead.id || lead.plot_request_id || lead.lead_id || lead._id;
    setBusy(true);
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Lead deleted successfully");
      setDeleteTarget(null);
      setSelectedLead(null);
      setMobileDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete lead");
    } finally {
      setBusy(false);
    }
  };

  const maskValue = (val, type) => {
    if (!val) return "—";
    if (type === "phone") {
      const p = String(val);
      if (p.length < 6) return "—";
      return p.replace(/(\d{3})\d+(\d{2})$/, "$1xxxxxx$2");
    }
    if (type === "email") {
      const parts = String(val).split("@");
      if (parts.length < 2) return "—";
      const name = parts[0];
      return `${name.substring(0, 2)}******@${parts[1]}`;
    }
    return val;
  };

  const handleCardClick = (lead, isMobile = false) => {
    setSelectedLead(lead);
    setSelectedTargetState("");
    setNote("");
    setEvents([]);
    loadEvents(lead.id || lead.plot_request_id || lead.lead_id || lead._id);
    if (isMobile) {
      setMobileDialogOpen(true);
    }
  };

  // Filter display leads based on user's role (Agents see only assigned and APPROVED leads; Admins/Office see all leads)
  const displayedLeads = useMemo(() => {
    return leads.filter(l => {
      if (!isAgent) return true;
      const isAssignedToMe = String(l.assigned_to_user_id || l.assigned_agent_id) === String(currentUserId);
      const isApproved = !l.assignment_status || l.assignment_status === "APPROVED";
      return isAssignedToMe && isApproved;
    });
  }, [leads, isAgent, currentUserId]);

  // Mobile filtered view (case-insensitive status check)
  const mobileFiltered = mobileFilter === "all" ? displayedLeads : displayedLeads.filter(l => columnKey(l) === mobileFilter);

  // Stats for mobile pills (case-insensitive status check)
  const counts = STATES.reduce((acc, s) => ({ ...acc, [s.key]: displayedLeads.filter(l => columnKey(l) === s.key).length }), {});

  return (
    <div className="flex flex-col h-[calc(100vh-4.5rem)] min-h-[600px] bg-background text-foreground animate-fade-in overflow-hidden" data-testid="leads-page">
      <div className="mb-3 shrink-0">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground mb-0.5">Operations · CRM</p>
        <h1 className="font-display text-3xl font-black tracking-tighter text-primary uppercase">Lead Management</h1>
        <p className="text-xs text-muted-foreground mt-0.5">Manage qualified buyer interests and follow up transitions.</p>
      </div>

      {!loading && error && <div className="mb-5 rounded-xl border border-destructive/30 bg-destructive/5 p-5 text-center shrink-0" role="alert"><p className="text-sm font-bold">Leads could not be loaded.</p><p className="mt-1 text-xs text-muted-foreground">{error}</p><Button type="button" size="sm" variant="outline" className="mt-3 gap-2" onClick={load}><RefreshCw className="h-4 w-4" aria-hidden="true"/>Retry</Button></div>}
      {!loading && !error && directoryError && <div className="mb-5 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 shrink-0" role="alert"><p className="text-xs font-bold">Assignment choices could not be loaded.</p><p className="mt-1 text-xs text-muted-foreground">{directoryError}</p></div>}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 flex-1">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-card border border-border/80 rounded-2xl p-4 h-96 animate-pulse space-y-3">
              <div className="h-4 bg-muted rounded w-1/2" />
              <div className="h-20 bg-muted/60 rounded-xl" />
              <div className="h-20 bg-muted/60 rounded-xl" />
            </div>
          ))}
        </div>
      ) : error ? null : displayedLeads.length === 0 && !selectedLead ? (
        <div className="border border-dashed border-border rounded-xl py-16 text-center bg-card flex-1">
          <Trophy className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No leads in your workspace yet.</p>
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0 relative overflow-hidden">
          
          {/* MOBILE VIEW LAYOUT */}
          <div className="block md:hidden flex-1 flex flex-col min-h-0">
            {/* Status Selector Horizontal Strip */}
            <div className="flex gap-2 overflow-x-auto pb-3 scrollbar-none shrink-0">
              <button
                type="button"
                onClick={() => setMobileFilter("all")}
                className={`px-4 py-1.5 rounded-full text-xs font-bold border shrink-0 transition ${
                  mobileFilter === "all" ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-foreground hover:bg-muted"
                }`}
                data-testid="lead-filter-all"
              >
                All <span className="ml-1 opacity-70 tabular-nums">{displayedLeads.length}</span>
              </button>
              {STATES.map(s => (
                <button
                  type="button"
                  key={s.key}
                  onClick={() => setMobileFilter(s.key)}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold border shrink-0 transition ${
                    mobileFilter === s.key ? "bg-primary text-primary-foreground border-primary" : `${s.color} hover:opacity-85`
                  }`}
                  data-testid={`lead-filter-${s.key}`}
                >
                  {s.label} <span className="ml-1 opacity-70 tabular-nums">{counts[s.key] || 0}</span>
                </button>
              ))}
            </div>

            {/* Mobile Card List */}
            <div className="flex-1 overflow-y-auto space-y-3 pb-6 mt-2">
              {mobileFiltered.map(lead => {
                const id = lead.id || lead.plot_request_id || lead.lead_id || lead._id;
                const assigned = isLeadAssignedToMe(lead);
                const normalizedStatus = String(lead.status || "new").toLowerCase();
                return (
                  <button
                    type="button"
                    key={id} 
                    onClick={() => handleCardClick(lead, true)}
                    className="w-full bg-card border border-border rounded-xl p-4 text-left active:scale-[0.98] transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    data-testid={`lead-row-${id}`}
                    aria-label={`Open lead for ${lead.buyer_name || lead.name || lead.contact_name || "buyer"}`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-display font-bold text-primary">{lead.buyer_name || lead.name || lead.contact_name || "—"}</h3>
                      <Badge variant="outline" className={STATE_LOOKUP[normalizedStatus]?.color || ""}>
                        {STATE_LOOKUP[normalizedStatus]?.label || lead.status}
                      </Badge>
                    </div>
                    {lead.is_plot_demand ? (
                      <div className="mt-1.5 text-xs text-muted-foreground bg-muted/40 p-2.5 rounded-lg space-y-1.5 border border-border/30 mb-2">
                        <div className="font-semibold text-primary flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                          <span className="truncate">{lead.raw?.location || lead.property_title || "Plots on Demand location"}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[10px]">
                          <div>Area: <span className="font-semibold text-foreground">{lead.raw?.area} {lead.raw?.area_unit || "sqyd"}</span></div>
                          <div>Budget: <span className="font-semibold text-emerald-600">₹{lead.raw?.budget ? parseFloat(lead.raw.budget).toLocaleString("en-IN") : "—"}</span></div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                        <Building2 className="w-3.5 h-3.5 shrink-0"/>
                        <span className="truncate">
                          {lead.property_title || 
                           (typeof lead.location === "string" ? lead.location : (lead.location?.locality || lead.location?.city || lead.location?.address)) || 
                           lead.property_id || 
                           "—"}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center text-xs pt-2 border-t border-border/40">
                      <span className="text-muted-foreground text-[10px]">
                        {lead.created_at ? new Date(lead.created_at).toLocaleDateString("en-IN") : ""}
                      </span>
                      {assigned ? (
                        <span className="text-emerald-500 font-semibold text-[11px] flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5"/> Assigned to Me</span>
                      ) : (
                        <span className="text-muted-foreground text-[11px]">
                          Agent: {lead.assigned_to_name || "Unassigned"}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
              {mobileFiltered.length === 0 && (
                <p className="text-muted-foreground text-center py-8">No leads in this status.</p>
              )}
            </div>
          </div>

          {/* DESKTOP VIEW KANBAN BOARD LAYOUT */}
          <div className="hidden md:flex flex-1 gap-4 overflow-x-auto pb-2 min-h-0 select-none scrollbar-none">
            {STATES.map(col => {
              const colLeads = displayedLeads.filter(l => columnKey(l) === col.key);
              return (
                <div key={col.key} className="flex flex-col w-72 shrink-0 h-full bg-muted/20 border border-border/40 rounded-2xl overflow-hidden transition-colors hover:border-border/70">
                  {/* Column Header with gradient stage accent */}
                  <div className="relative p-3 border-b border-border/30 bg-card shrink-0 flex justify-between items-center">
                    <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r ${col.accent} to-transparent`} />
                    <h3 className="font-display text-sm font-bold text-foreground flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${col.dot} shadow-[0_0_0_3px] shadow-current/10`} />
                      {col.label}
                    </h3>
                    <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full font-bold transition-colors ${colLeads.length > 0 ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>{colLeads.length}</span>
                  </div>

                  {/* Column Cards */}
                  <div className="flex-1 p-2 space-y-2 overflow-y-auto scrollbar-none bg-background/5 min-h-0">
                    {colLeads.map((lead, ci) => {
                      const id = lead.id || lead.plot_request_id || lead.lead_id || lead._id;
                      const assigned = isLeadAssignedToMe(lead);
                      const isSelected = selectedLead && (selectedLead.id || selectedLead.plot_request_id || selectedLead.lead_id || selectedLead._id) === id;
                      return (
                        <button
                          type="button"
                          key={id}
                          onClick={() => handleCardClick(lead, false)}
                          style={{ animationDelay: `${Math.min(ci, 8) * 45}ms`, animationFillMode: "both" }}
                          className={`w-full bg-card p-4 rounded-xl border text-left transition-all cursor-pointer animate-fade-up hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                            isSelected ? "border-primary ring-1 ring-primary shadow-md" : "border-border hover:border-primary/50 shadow-sm hover:shadow-md"
                          }`}
                          data-testid={`lead-row-${id}`}
                          aria-pressed={Boolean(isSelected)}
                        >
                          <div className="flex justify-between items-start gap-2 mb-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="w-6 h-6 rounded-full overflow-hidden border border-border bg-muted flex items-center justify-center shrink-0 relative">
                                {lead.buyer_avatar_url ? (
                                  <img 
                                    src={lead.buyer_avatar_url} 
                                    alt="" 
                                    loading="lazy" 
                                    decoding="async" 
                                    className="w-full h-full object-cover" 
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                      if (e.currentTarget.nextElementSibling) e.currentTarget.nextElementSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <span 
                                  className="text-[10px] font-bold text-primary bg-primary/10 w-full h-full flex items-center justify-center"
                                  style={{ display: lead.buyer_avatar_url ? 'none' : 'flex' }}
                                >
                                  {String(lead.buyer_name || lead.name || "—").charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <h4 className="font-display font-bold text-sm text-primary leading-tight truncate">{lead.buyer_name || lead.name || lead.contact_name || "—"}</h4>
                            </div>
                            <span className="text-[9px] text-muted-foreground font-mono shrink-0">{lead.created_at ? new Date(lead.created_at).toLocaleDateString("en-IN") : ""}</span>
                          </div>
                          
                          <div className="mt-1.5 text-xs text-muted-foreground bg-muted/40 p-2 rounded-lg flex gap-2 border border-border/30 mb-3 min-w-0">
                            {lead.property_thumbnail_url && (
                              <div className="w-10 h-10 rounded-md bg-secondary overflow-hidden shrink-0 border border-border/50 shadow-sm">
                                <img 
                                  src={lead.property_thumbnail_url} 
                                  alt="" 
                                  loading="lazy" 
                                  decoding="async" 
                                  className="w-full h-full object-cover" 
                                  onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                                />
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="font-semibold text-primary flex items-center gap-1.5 text-xs">
                                {lead.is_plot_demand ? (
                                  <MapPin className="w-3 h-3 text-blue-400 shrink-0" />
                                ) : (
                                  <Building2 className="w-3 h-3 text-blue-400 shrink-0" />
                                )}
                                <span className="truncate">{lead.raw?.location || lead.property_title || "Requirement"}</span>
                              </div>
                              <div className="grid grid-cols-2 gap-x-2 text-[10px] text-muted-foreground mt-0.5">
                                <div>Area: <span className="font-medium text-foreground">{lead.raw?.area || "—"} {lead.raw?.area_unit || (lead.is_plot_demand ? "sqyd" : "sqft")}</span></div>
                                <div>Budget: <span className="font-medium text-emerald-600">₹{lead.raw?.budget ? parseFloat(lead.raw.budget).toLocaleString("en-IN") : "—"}</span></div>
                              </div>
                            </div>
                          </div>

                          <div className="flex justify-between items-center text-[10px] font-mono border-t border-border/30 pt-2">
                            {assigned ? (
                              <span className="text-emerald-500 font-semibold flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5"/> Assigned to Me</span>
                            ) : (
                              <div className="flex items-center gap-1.5 text-muted-foreground truncate max-w-[180px]">
                                <div className="w-5 h-5 rounded-full overflow-hidden border border-border bg-muted flex items-center justify-center shrink-0 relative">
                                  {lead.assigned_to_avatar_url ? (
                                    <img 
                                      src={lead.assigned_to_avatar_url} 
                                      alt="" 
                                      loading="lazy" 
                                      decoding="async" 
                                      className="w-full h-full object-cover" 
                                      onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                        if (e.currentTarget.nextElementSibling) e.currentTarget.nextElementSibling.style.display = 'flex';
                                      }}
                                    />
                                  ) : null}
                                  <span 
                                    className="text-[8px] font-bold text-primary bg-primary/10 w-full h-full flex items-center justify-center"
                                    style={{ display: lead.assigned_to_avatar_url ? 'none' : 'flex' }}
                                  >
                                    {String(lead.assigned_to_name || "?").charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <span>Agent: {lead.assigned_to_name || "Unassigned"}</span>
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                    {colLeads.length === 0 && (
                      <div className="text-center py-8 text-xs text-muted-foreground italic">No leads</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* DESKTOP SIDE DRAWER PANEL */}
          {selectedLead && (
            <div className="hidden md:flex absolute top-0 right-0 bottom-0 w-96 bg-card border-l border-border rounded-2xl shadow-2xl flex-col z-20 animate-fade-in">
              {/* Header */}
              <div className="p-4 border-b border-border flex justify-between items-start bg-muted/10 shrink-0">
                <div className="min-w-0">
                  <h2 className="font-display text-lg font-bold text-primary truncate">{selectedLead.buyer_name || selectedLead.name || selectedLead.contact_name || "—"}</h2>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                    {selectedLead.is_plot_demand ? (
                      <>
                        <MapPin className="w-3.5 h-3.5 shrink-0 text-blue-400"/>
                        <Link 
                          to={`/dashboard/admin/plots-on-demand/workbench?select=${selectedLead.plot_demand_id || selectedLead.plot_request_id || selectedLead.id || selectedLead._id}`}
                          title="Click to view Plots on Demand request"
                          className="truncate hover:underline text-blue-400 font-medium"
                        >
                          {selectedLead.raw?.location || selectedLead.property_title}
                        </Link>
                      </>
                    ) : (
                      <>
                        <Building2 className="w-3.5 h-3.5 shrink-0"/>
                        {selectedLead.property_id && selectedLead.property_id !== "null" ? (
                          <Link 
                            to={`/dashboard/admin/properties?select=${selectedLead.property_id}`}
                            title="Click to view Property details"
                            className="truncate hover:underline text-blue-400 font-medium"
                          >
                            {selectedLead.property_title || selectedLead.property_id}
                          </Link>
                        ) : (
                          <span className="truncate">{selectedLead.property_title || selectedLead.property_id}</span>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <button 
                  type="button"
                  onClick={() => setSelectedLead(null)}
                  className="text-muted-foreground hover:text-foreground p-1 rounded-full hover:bg-muted/50"
                  aria-label="Close lead details"
                  title="Close lead details"
                >
                  <X className="w-5 h-5"/>
                </button>
              </div>

              {/* Drawer Content */}
              <div className="flex-1 overflow-y-auto p-4 space-y-5 bg-background/20">
                
                {/* Status Indicator */}
                <div className="flex justify-between items-center bg-card p-3 rounded-xl border border-border">
                  <span className="text-xs font-semibold text-muted-foreground">Current Status</span>
                  <Badge variant="outline" className={STATE_LOOKUP[String(selectedLead.status || "new").toLowerCase()]?.color || ""}>
                    {STATE_LOOKUP[String(selectedLead.status || "new").toLowerCase()]?.label || selectedLead.status}
                  </Badge>
                </div>

                {/* Agent Assignment Info */}
                {renderAssignmentBlock({
                  selectedLead,
                  setSelectedLead,
                  agents,
                  offices,
                  isAdmin,
                  isOffice,
                  currentUserOfficeId,
                  busy,
                  setBusy,
                  load,
                  setMobileDialogOpen,
                  isMobile: false,
                  api,
                  toast,
                  Button,
                  User,
                  ShieldAlert,
                  proposalRejectReason,
                  setProposalRejectReason
                })}

                {/* Plot Demand Specs card (if plot demand) */}
                {selectedLead.is_plot_demand && (
                  <div className="bg-card border border-border rounded-xl p-4 space-y-3">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground border-b border-border/40 pb-2 flex items-center gap-1.5">
                      <MapPin className="w-4 h-4 text-blue-400" /> Plot Requirements
                    </h3>
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div>
                        <span className="text-muted-foreground block font-medium">Budget Limit</span>
                        <span className="text-sm font-bold text-emerald-600">₹{selectedLead.raw?.budget ? parseFloat(selectedLead.raw.budget).toLocaleString("en-IN") : "—"}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block font-medium">Required Area</span>
                        <span className="text-sm font-bold text-foreground">{selectedLead.raw?.area} {selectedLead.raw?.area_unit || "sqyd"}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block font-medium">Buying Purpose</span>
                        <span className="text-sm font-semibold capitalize text-foreground">{selectedLead.raw?.purpose || "—"}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block font-medium">Timeframe</span>
                        <span className="text-sm font-semibold capitalize text-foreground">{selectedLead.raw?.timeframe || "—"}</span>
                      </div>
                      {selectedLead.raw?.road_width && (
                        <div>
                          <span className="text-muted-foreground block font-medium">Road Width</span>
                          <span className="text-sm font-semibold text-foreground">{selectedLead.raw?.road_width}</span>
                        </div>
                      )}
                      {selectedLead.raw?.facing && (
                        <div>
                          <span className="text-muted-foreground block font-medium">Facing Preference</span>
                          <span className="text-sm font-semibold text-foreground">{selectedLead.raw?.facing}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Quick Actions (Call/Email/WhatsApp) */}
                <div className="grid grid-cols-3 gap-2">
                  <ContactAction
                    href={canInteractWithLead(selectedLead) && selectedLead.buyer_phone ? `tel:${selectedLead.buyer_phone}` : ""}
                    label="Call"
                    icon={Phone}
                    reason={!canInteractWithLead(selectedLead) ? "Contact options are restricted for leads assigned to other agents." : "No buyer phone number is available."}
                  />
                  <ContactAction
                    href={canInteractWithLead(selectedLead) && selectedLead.buyer_email ? `mailto:${selectedLead.buyer_email}` : ""}
                    label="Email"
                    icon={Mail}
                    reason={!canInteractWithLead(selectedLead) ? "Contact options are restricted for leads assigned to other agents." : "No buyer email address is available."}
                  />
                  <ContactAction
                    href={canInteractWithLead(selectedLead) && selectedLead.buyer_phone ? `https://wa.me/${String(selectedLead.buyer_phone).replace(/\D/g, "")}` : ""}
                    label="WhatsApp"
                    icon={MessageCircle}
                    external
                    success
                    reason={!canInteractWithLead(selectedLead) ? "Contact options are restricted for leads assigned to other agents." : "No buyer phone number is available."}
                  />
                </div>

                {/* Contact Info Card */}
                <div className="bg-card border border-border rounded-xl p-4 space-y-3">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground border-b border-border/40 pb-2">Contact Info</h3>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground text-xs">Phone</span>
                      <span className="font-mono text-primary font-bold">
                        {canInteractWithLead(selectedLead) ? (selectedLead.buyer_phone || "—") : maskValue(selectedLead.buyer_phone, "phone")}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground text-xs">Email</span>
                      <span className="font-mono text-primary font-bold truncate max-w-[180px]">
                        {canInteractWithLead(selectedLead) ? (selectedLead.buyer_email || "—") : maskValue(selectedLead.buyer_email, "email")}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground text-xs">Inquiry Source</span>
                      <span className="text-foreground capitalize">{selectedLead.source || "Website"}</span>
                    </div>
                    {selectedLead.buyer_user_id && (
                      <div className="flex justify-between items-center text-sm border-t border-border/20 pt-1.5 mt-1.5">
                        <span className="text-muted-foreground text-xs">Buyer Profile</span>
                        <Link 
                          to={`/dashboard/admin/users?select=${selectedLead.buyer_user_id}`}
                          title="Click to view buyer profile"
                          className="font-bold text-primary hover:underline"
                        >
                          View Profile
                        </Link>
                      </div>
                    )}
                  </div>
                </div>

                {/* Update Status Actions */}
                <div className="bg-card border border-border rounded-xl p-4 space-y-4">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground border-b border-border/40 pb-2">Update Status</h3>
                  
                  {canInteractWithLead(selectedLead) ? (
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <label className="text-xs text-muted-foreground font-semibold">Select New State</label>
                        <select 
                          value={selectedTargetState}
                          onChange={(e) => setSelectedTargetState(e.target.value)}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        >
                          <option value="">-- Choose status --</option>
                          {(NEXT[String(selectedLead.status || "new").toLowerCase()] || []).map(n => (
                            <option key={n} value={n}>{STATE_LOOKUP[n.toLowerCase()]?.label || n}</option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-1">
                        <label className="text-xs text-muted-foreground font-semibold">Action Note</label>
                        <textarea
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          placeholder="e.g. Spoke to buyer, visit scheduled for Saturday"
                          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[80px]"
                        />
                      </div>

                      <Button 
                        disabled={!selectedTargetState || busy} 
                        onClick={() => handleTransition(selectedTargetState)}
                        className="w-full font-bold mt-2"
                      >
                        {busy ? "Saving…" : "Submit Status Change"}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2 p-3 bg-rose-950/20 text-rose-300 border border-rose-900/40 rounded-xl text-xs">
                      <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>Status updates and transitions are locked. You must be the assigned agent or authorized office/admin to update status.</span>
                    </div>
                  )}
                </div>

                {/* Lead Action History & Notes Timeline */}
                <div className="bg-card border border-border rounded-xl p-4 space-y-3">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground border-b border-border/40 pb-2">Action History &amp; Notes</h3>
                  <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
                    {!canInteractWithLead(selectedLead) ? (
                      <div className="text-xs text-muted-foreground italic">Restricted (Notes are visible to assigned agent or admin only)</div>
                    ) : eventsError ? (
                      <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs" role="alert"><p>Lead history could not be loaded: {eventsError}</p><Button type="button" size="xs" variant="outline" className="mt-2 gap-1" onClick={() => loadEvents(selectedLeadId)}><RefreshCw className="h-3 w-3" aria-hidden="true"/>Retry</Button></div>
                    ) : events.length === 0 ? (
                      <div className="text-xs text-muted-foreground italic">No action notes or history recorded yet.</div>
                    ) : (
                      events.map((e) => (
                        <div key={e.id} className="text-xs bg-muted/30 p-2.5 rounded-lg border border-border/30">
                          <div className="flex justify-between text-[10px] text-muted-foreground mb-1 font-mono">
                            <span className="font-semibold text-foreground">{e.actor_name || "System"}</span>
                            <span>{new Date(e.created_at).toLocaleString("en-IN")}</span>
                          </div>
                          <p className="text-foreground whitespace-pre-wrap">{cleanNoteText(e.note, e.actor_name) || e.event_type}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Danger Zone (Delete Lead - Admin/Office only) */}
                {(isAdmin || isOffice) && (
                  <div className="bg-card border border-rose-900/40 rounded-xl p-4 space-y-3">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-rose-400 border-b border-rose-900/20 pb-2">Danger Zone</h3>
                    <Button 
                      variant="destructive"
                      disabled={busy}
                      onClick={() => setDeleteTarget(selectedLead)}
                      className="w-full font-bold bg-rose-950/40 hover:bg-rose-900/60 border border-rose-800 text-rose-200"
                    >
                      Delete Lead
                    </Button>
                  </div>
                )}

              </div>
            </div>
          )}

          {/* MOBILE DIALOG DETAILS */}
          <Dialog open={mobileDialogOpen} onOpenChange={(o) => { if(!o) { setMobileDialogOpen(false); setSelectedLead(null); } }}>
            <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="lead-transition-dialog">
              {selectedLead && (
                <>
                  <DialogHeader>
                    <DialogTitle className="font-display tracking-tight text-primary">{selectedLead.buyer_name || selectedLead.name || selectedLead.contact_name}</DialogTitle>
                    <DialogDescription className="flex items-center gap-1 text-xs">
                      {selectedLead.is_plot_demand ? (
                        <>
                          <MapPin className="w-3.5 h-3.5 shrink-0 text-blue-400"/>
                          <span className="truncate">{selectedLead.raw?.location || selectedLead.property_title}</span>
                        </>
                      ) : (
                        <>
                          <Building2 className="w-3.5 h-3.5 shrink-0"/>
                          <span className="truncate">{selectedLead.property_title || selectedLead.property_id}</span>
                        </>
                      )}
                    </DialogDescription>
                  </DialogHeader>

                  <div className="space-y-4 pt-3">
                    <div className="flex justify-between items-center text-sm border-b border-border/40 pb-2">
                      <span className="text-muted-foreground">Current Status</span>
                      <Badge variant="outline" className={STATE_LOOKUP[String(selectedLead.status || "new").toLowerCase()]?.color || ""}>
                        {STATE_LOOKUP[String(selectedLead.status || "new").toLowerCase()]?.label || selectedLead.status}
                      </Badge>
                    </div>

                    {selectedLead.is_plot_demand && (
                      <div className="bg-card border border-border rounded-xl p-3.5 space-y-2.5">
                        <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5 text-blue-400" /> Plot Requirements
                        </h4>
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          <div>
                            <span className="text-muted-foreground block font-medium">Budget Limit</span>
                            <span className="text-[13px] font-bold text-emerald-600">₹{selectedLead.raw?.budget ? parseFloat(selectedLead.raw.budget).toLocaleString("en-IN") : "—"}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground block font-medium">Required Area</span>
                            <span className="text-[13px] font-bold text-foreground">{selectedLead.raw?.area} {selectedLead.raw?.area_unit || "sqyd"}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground block font-medium">Buying Purpose</span>
                            <span className="text-[13px] font-semibold capitalize text-foreground">{selectedLead.raw?.purpose || "—"}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground block font-medium">Timeframe</span>
                            <span className="text-[13px] font-semibold capitalize text-foreground">{selectedLead.raw?.timeframe || "—"}</span>
                          </div>
                          {selectedLead.raw?.road_width && (
                            <div>
                              <span className="text-muted-foreground block font-medium">Road Width</span>
                              <span className="text-[13px] font-semibold text-foreground">{selectedLead.raw?.road_width}</span>
                            </div>
                          )}
                          {selectedLead.raw?.facing && (
                            <div>
                              <span className="text-muted-foreground block font-medium">Facing Preference</span>
                              <span className="text-[13px] font-semibold text-foreground">{selectedLead.raw?.facing}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Contact details</h4>
                      <div className="space-y-1 bg-muted/30 p-3 rounded-lg text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground text-xs">Phone</span>
                          <span className="font-mono font-semibold">
                            {canInteractWithLead(selectedLead) ? (selectedLead.buyer_phone || "—") : maskValue(selectedLead.buyer_phone, "phone")}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground text-xs">Email</span>
                          <span className="font-mono font-semibold">
                            {canInteractWithLead(selectedLead) ? (selectedLead.buyer_email || "—") : maskValue(selectedLead.buyer_email, "email")}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Mobile Assignment Details */}
                    {renderAssignmentBlock({
                      selectedLead,
                      setSelectedLead,
                      agents,
                      offices,
                      isAdmin,
                      isOffice,
                      currentUserOfficeId,
                      busy,
                      setBusy,
                      load,
                      setMobileDialogOpen,
                      isMobile: true,
                      api,
                      toast,
                      Button,
                      User,
                      ShieldAlert,
                      proposalRejectReason,
                      setProposalRejectReason
                    })}

                    {canInteractWithLead(selectedLead) ? (
                      <div className="space-y-3 pt-2">
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground font-semibold">Change Status To</label>
                          <select 
                            value={selectedTargetState}
                            onChange={(e) => setSelectedTargetState(e.target.value)}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          >
                            <option value="">-- Choose status --</option>
                            {(NEXT[String(selectedLead.status || "new").toLowerCase()] || []).map(n => (
                              <option key={n} value={n}>{STATE_LOOKUP[n.toLowerCase()]?.label || n}</option>
                            ))}
                          </select>
                        </div>

                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground font-semibold">Note</label>
                          <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Add action details..."
                            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[80px]"
                          />
                        </div>

                        <Button 
                          disabled={!selectedTargetState || busy} 
                          onClick={async () => {
                            await handleTransition(selectedTargetState);
                            setMobileDialogOpen(false);
                          }}
                          className="w-full font-bold"
                        >
                          {busy ? "Saving…" : "Save Status"}
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2 p-3 bg-rose-950/20 text-rose-300 border border-rose-900/40 rounded-xl text-xs">
                        <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>Status transitions are locked. You must be the assigned agent or authorized office/admin to update status.</span>
                      </div>
                    )}

                    {/* Action History timeline on Mobile */}
                    <div className="pt-3 border-t border-border/40 space-y-2">
                      <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Action History &amp; Notes</h4>
                      <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                        {!canInteractWithLead(selectedLead) ? (
                          <div className="text-xs text-muted-foreground italic">Restricted (Notes are visible to assigned agent or admin only)</div>
                        ) : eventsError ? (
                          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs" role="alert"><p>Lead history could not be loaded: {eventsError}</p><Button type="button" size="xs" variant="outline" className="mt-2 gap-1" onClick={() => loadEvents(selectedLeadId)}><RefreshCw className="h-3 w-3" aria-hidden="true"/>Retry</Button></div>
                        ) : events.length === 0 ? (
                          <div className="text-xs text-muted-foreground italic">No action notes or history recorded yet.</div>
                        ) : (
                          events.map((e) => (
                            <div key={e.id} className="text-xs bg-muted/30 p-2 rounded border border-border/30">
                              <div className="flex justify-between text-[9px] text-muted-foreground mb-1 font-mono">
                                <span className="font-semibold text-foreground">{e.actor_name || "System"}</span>
                                <span>{new Date(e.created_at).toLocaleString("en-IN")}</span>
                              </div>
                              <p className="text-foreground whitespace-pre-wrap">{cleanNoteText(e.note, e.actor_name) || e.event_type}</p>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {(isAdmin || isOffice) && (
                      <div className="pt-2 border-t border-border/40">
                        <Button 
                          variant="destructive"
                          disabled={busy}
                          onClick={() => setDeleteTarget(selectedLead)}
                          className="w-full font-bold bg-rose-950/40 hover:bg-rose-900/60 border border-rose-800 text-rose-200"
                        >
                          Delete Lead
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>
          <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
            <DialogContent className="sm:max-w-md" data-testid="delete-lead-dialog">
              <DialogHeader>
                <DialogTitle>Archive this lead?</DialogTitle>
                <DialogDescription>
                  The lead for {deleteTarget?.buyer_name || "this buyer"} will be removed from active CRM views. Linked Plots on Demand records will be cancelled.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2 sm:gap-2">
                <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)}>Keep lead</Button>
                <Button type="button" variant="destructive" disabled={busy} onClick={() => handleDeleteLead(deleteTarget)}>{busy ? "Archiving…" : "Archive lead"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  );
}

function ContactAction({ href, label, icon: Icon, reason, external = false, success = false }) {
  const shared = "flex min-h-16 flex-col items-center justify-center rounded-xl border p-3 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  if (!href) {
    return (
      <button type="button" onClick={() => toast.info(reason)} title={reason} className={`${shared} cursor-help border-border/30 bg-muted/50 text-muted-foreground/60`}>
        <Icon className="mb-1 h-4 w-4" aria-hidden="true"/>
        <span className="text-[10px] font-bold">{label}</span>
      </button>
    );
  }
  return (
    <a href={href} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined} title={`${label} buyer`} className={`${shared} bg-card text-foreground ${success ? "hover:border-emerald-500 hover:text-emerald-500" : "hover:border-primary hover:text-primary"}`}>
      <Icon className="mb-1 h-4 w-4" aria-hidden="true"/>
      <span className="text-[10px] font-bold">{label}</span>
    </a>
  );
}

const renderAssignmentBlock = ({
  selectedLead,
  setSelectedLead,
  agents,
  offices,
  isAdmin,
  isOffice,
  currentUserOfficeId,
  busy,
  setBusy,
  load,
  setMobileDialogOpen,
  isMobile,
  api,
  toast,
  Button,
  User,
  ShieldAlert,
  proposalRejectReason,
  setProposalRejectReason
}) => {
  if (!selectedLead) return null;
  const id = selectedLead.id || selectedLead.plot_request_id || selectedLead.lead_id || selectedLead._id;
  const proposedAgent = agents.find(a => String(a.user_id || a.id) === String(selectedLead.assigned_to_user_id));
  const proposedOfficeId = proposedAgent?.office_id || proposedAgent?.raw?.office_id || "";
  
  const currentOfficeId = isOffice
    ? (currentUserOfficeId || "")
    : (selectedLead.assigned_office_id || (selectedLead.assignment_status === "PROPOSED" ? proposedOfficeId : ""));
  const currentAgentId = selectedLead.assigned_agent_id || (selectedLead.assignment_status === "PROPOSED" ? selectedLead.assigned_to_user_id : "");

  return (
    <div className="bg-card p-3.5 rounded-xl border border-border space-y-3 text-xs text-left">
      <span className="text-xs font-semibold text-muted-foreground block border-b border-border/40 pb-1.5">Assignment Details</span>
      
      {selectedLead.assignment_status === "PROPOSED" ? (
        <div className="bg-amber-500/10 border border-amber-500/20 p-2.5 rounded-lg text-xs space-y-1">
          <div className="font-bold text-amber-500 flex items-center gap-1">
            <ShieldAlert className="w-3.5 h-3.5 shrink-0"/> Proposal Suggestion Received
          </div>
          <div className="text-foreground">
            Proposed Agent: <Link to={`/dashboard/admin/users?select=${selectedLead.assigned_to_user_id}`} title="Click to view agent profile" className="font-semibold text-primary hover:underline">{selectedLead.assigned_to_name || "Unknown"}</Link>
          </div>
          {selectedLead.proposed_note && (
            <div className="text-muted-foreground italic text-[10px]">
              Note: "{selectedLead.proposed_note}"
            </div>
          )}
        </div>
      ) : (
        <div className="text-xs font-semibold flex items-center gap-1.5 text-primary mb-1">
          <User className="w-4 h-4 text-muted-foreground shrink-0"/>
          {selectedLead.assigned_to_name || selectedLead.assigned_agent_name ? (
            <span>Assigned Agent: <Link to={`/dashboard/admin/users?select=${selectedLead.assigned_agent_id || selectedLead.assigned_to_user_id}`} title="Click to view agent profile" className="font-bold text-primary hover:underline">{selectedLead.assigned_to_name || selectedLead.assigned_agent_name}</Link></span>
          ) : (
            <span className="text-muted-foreground">Unassigned Lead</span>
          )}
        </div>
      )}

      {(isAdmin || isOffice) && (
        <div className="space-y-2 text-xs pt-1 border-t border-border/20">
          {/* Office Selection */}
          <div className="space-y-1">
            <label className="text-[9px] font-bold text-muted-foreground uppercase">Assign Office</label>
            <select
              aria-label="Assign office"
              value={currentOfficeId}
              onChange={(e) => {
                setSelectedLead({ 
                  ...selectedLead, 
                  assigned_office_id: e.target.value, 
                  assigned_agent_id: ""
                });
              }}
              className={`flex w-full rounded-md border border-input bg-background px-2.5 py-1 text-xs ${isMobile ? "h-9" : "h-8"}`}
              disabled={isOffice}
              title={isOffice ? "Office users can assign leads only within their own office" : "Choose the responsible office"}
            >
              <option value="">-- Choose Office --</option>
              {offices.map(o => (
                <option key={o.office_id || o.id} value={o.office_id || o.id}>{o.name}</option>
              ))}
            </select>
          </div>

          {/* Agent Selection */}
          <div className="space-y-1">
            <label className="text-[9px] font-bold text-muted-foreground uppercase">Assign Agent</label>
            <select
              aria-label="Assign agent"
              value={currentAgentId}
              onChange={(e) => {
                setSelectedLead({ 
                  ...selectedLead, 
                  assigned_agent_id: e.target.value 
                });
              }}
              className={`flex w-full rounded-md border border-input bg-background px-2.5 py-1 text-xs ${isMobile ? "h-9" : "h-8"}`}
              disabled={!currentOfficeId}
            >
              <option value="">-- Choose Agent --</option>
              {agents
                .filter(a => String(a.office_id || a.raw?.office_id) === String(currentOfficeId))
                .map(a => (
                  <option key={a.user_id || a.id} value={a.user_id || a.id}>{a.name}</option>
                ))
              }
            </select>
          </div>

          {/* Action Buttons */}
          {selectedLead.assignment_status === "PROPOSED" && !isAdmin ? (
            <div className="flex justify-end pt-2">
              <span className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs font-bold text-amber-700">
                Awaiting admin approval
              </span>
            </div>
          ) : selectedLead.assignment_status === "PROPOSED" ? (
            <div className="space-y-2 pt-2">
              <label className="block text-[10px] font-bold uppercase text-muted-foreground" htmlFor={`proposal-rejection-${id}`}>Rejection reason</label>
              <textarea id={`proposal-rejection-${id}`} value={proposalRejectReason} onChange={(event) => setProposalRejectReason(event.target.value)} maxLength={1000} rows={2} placeholder="Required before rejecting this proposal" className="w-full resize-y rounded-md border border-input bg-background px-2.5 py-2 text-xs" />
              <div className="flex gap-2 justify-end">
              <Button 
                size="xs" 
                variant="outline" 
                className="border-rose-300 text-rose-700 hover:bg-rose-50 font-bold"
                onClick={async () => {
                  const reason = proposalRejectReason.trim();
                  if (!reason) { toast.error("Enter a rejection reason"); return; }
                  setBusy(true);
                  try {
                    await api.post(`/v1/admin/crm/leads/${id}/reject-assignment`, { reason });
                    toast.success("Assignment rejected");
                    setProposalRejectReason("");
                    if (isMobile) setMobileDialogOpen(false);
                    setSelectedLead(null);
                    load();
                  } catch (e) {
                    toast.error("Failed to reject proposal");
                  } finally {
                    setBusy(false);
                  }
                }}
                disabled={busy || !proposalRejectReason.trim()}
              >
                Reject
              </Button>
              {(!selectedLead.assigned_agent_id || selectedLead.assigned_agent_id === selectedLead.assigned_to_user_id) && 
               (!selectedLead.assigned_office_id || selectedLead.assigned_office_id === proposedOfficeId) ? (
                <Button 
                  size="xs" 
                  className="font-bold"
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await api.post(`/v1/admin/crm/leads/${id}/approve-assignment`);
                      toast.success("Assignment approved");
                      if (isMobile) setMobileDialogOpen(false);
                      setSelectedLead(null);
                      load();
                    } catch (e) {
                      toast.error("Failed to approve proposal");
                    } finally {
                      setBusy(false);
                    }
                  }}
                  disabled={busy}
                >
                  Approve
                </Button>
              ) : (
                <Button 
                  size="xs" 
                  className="font-bold bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={async () => {
                    if (!selectedLead.assigned_office_id || !selectedLead.assigned_agent_id) {
                      toast.error("Select office and agent to assign");
                      return;
                    }
                    setBusy(true);
                    try {
                      await api.post(`/office/crm/leads/${id}/propose-assignment`, {
                        office_id: selectedLead.assigned_office_id,
                        agent_user_id: selectedLead.assigned_agent_id,
                        note: "Admin override assignment from Kanban dashboard"
                      });
                      toast.success("Assignment override saved!");
                      if (isMobile) setMobileDialogOpen(false);
                      setSelectedLead(null);
                      load();
                    } catch (e) {
                      toast.error("Failed to override assignment");
                    } finally {
                      setBusy(false);
                    }
                  }}
                  disabled={busy}
                >
                  Approve &amp; Override
                </Button>
              )}
              </div>
            </div>
          ) : (
            <div className="flex justify-end pt-1">
              <Button
                size="xs"
                className="font-bold"
                onClick={async () => {
                  const finalOfficeId = currentOfficeId;
                  const finalAgentId = currentAgentId;
                  if (!finalOfficeId || !finalAgentId) {
                    toast.error("Select office and agent to assign");
                    return;
                  }
                  setBusy(true);
                  try {
                    await api.post(`/office/crm/leads/${id}/propose-assignment`, {
                      office_id: finalOfficeId,
                      agent_user_id: finalAgentId,
                      note: "Admin direct assignment from Kanban dashboard"
                    });
                    toast.success("Assignment saved successfully!");
                    if (isMobile) setMobileDialogOpen(false);
                    setSelectedLead(null);
                    load();
                  } catch (e) {
                    toast.error("Failed to save assignment");
                  } finally {
                    setBusy(false);
                  }
                }}
                disabled={busy || !currentOfficeId || !currentAgentId}
              >
                Save Assignment
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
